<template>
  <div
    v-if="subkrewe.expected?.captains && subkrewe.expected.captains.length > 0 && !(subkrewe.captains && subkrewe.captains.length > 0)"
    class="kb-row"
  >
    <div class="kb-row-body mt-0">
      <div>
        <h4 class="kb-row-title">
          Expected captains
        </h4>
        <p class="kb-row-hint">
          Named when the SubKrewe registered; they become captains by signing up with this email
        </p>
      </div>

      <div class="kb-list-head">
        <span class="kb-row-meta">{{ subkrewe.expected.captains.length }} expected</span>
        <div class="flex flex-wrap items-center gap-1">
          <Button
            v-if="subkrewe.captains?.length === 0"
            type="button"
            label="Email them"
            icon="pi pi-envelope"
            text
            severity="secondary"
            size="small"
            @click="contactExpectedCaptains"
          />
          <Button
            type="button"
            label="Add"
            icon="pi pi-plus"
            text
            size="small"
            @click="openAddCaptainDialog"
          />
        </div>
      </div>

      <ul class="kb-rows m-0 p-0 list-none">
        <li
          v-for="(expectedCaptain, index) in subkrewe.expected.captains"
          :key="expectedCaptain.email"
          class="kb-row kb-row-link"
        >
          <button
            type="button"
            class="kb-row-open"
            @click="openEditCaptainDialog(index, expectedCaptain)"
          >
            <span class="kb-row-title block">{{ expectedCaptain.name }}</span>
            <span class="kb-row-meta block mt-0.5">
              {{ expectedCaptain.email }}<template v-if="expectedCaptain.phone"> · {{ expectedCaptain.phone }}</template>
            </span>
            <span
              v-if="!isCaptainEmailMatched(expectedCaptain.email)"
              class="block mt-0.5 text-xs text-kb-gold"
            >
              <i class="pi pi-exclamation-triangle !text-[10px] mr-1" />Hasn't claimed the SubKrewe yet
            </span>
          </button>
          <div class="kb-row-aside">
            <i
              class="pi pi-angle-right kb-row-chevron"
              aria-hidden="true"
            />
          </div>
        </li>
      </ul>

      <!-- Declined information -->
      <p
        v-if="subkrewe.declined_by"
        class="kb-row-note"
      >
        Declined by {{ subkrewe.declined_by }}<template v-if="subkrewe.decline_reason">
          : {{ subkrewe.decline_reason }}
        </template>
      </p>
    </div>
  </div>

  <!-- Add / edit an expected captain -->
  <Dialog
    v-model:visible="showEditDialog"
    :header="isAddMode ? 'Add expected captain' : 'Edit expected captain'"
    modal
    :style="{ width: '28rem' }"
    :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
  >
    <div class="kb-form">
      <div class="kb-field">
        <label for="name">Name</label>
        <InputText
          id="name"
          v-model="editingCaptain.name"
          size="small"
          fluid
          autofocus
          required
        />
      </div>

      <div class="kb-form-pair">
        <div class="kb-field">
          <label for="email">Email</label>
          <InputText
            id="email"
            v-model="editingCaptain.email"
            type="email"
            size="small"
            fluid
            required
          />
        </div>

        <div class="kb-field">
          <label for="phone">Phone <span class="kb-field-optional">(optional)</span></label>
          <InputText
            id="phone"
            v-model="editingCaptain.phone"
            size="small"
            fluid
          />
        </div>
      </div>
    </div>

    <template #footer>
      <Button
        v-if="!isAddMode"
        type="button"
        label="Remove"
        icon="pi pi-trash"
        text
        severity="danger"
        class="mr-auto"
        @click="removeExpectedCaptain"
      />
      <Button
        type="button"
        label="Cancel"
        text
        severity="secondary"
        @click="closeEditDialog"
      />
      <Button
        type="button"
        :label="isAddMode ? 'Add' : 'Save'"
        @click="saveExpectedCaptain"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast'
import { reactive, ref } from 'vue'
import type { SubKrewe } from '~/shared/types/subkrewe'
import { useSubKrewesStore } from '~/stores/subkrewes'

interface ExpectedCaptain {
  name: string
  email: string
  phone?: string | null
}

interface Props {
  subkrewe: SubKrewe
}

const props = defineProps<Props>()
const toast = useToast()
const subkrewesStore = useSubKrewesStore()

// Dialog state
const showEditDialog = ref(false)
const isAddMode = ref(false)
const editingIndex = ref(-1)
const editingCaptain = reactive<ExpectedCaptain>({
  name: '',
  email: '',
  phone: '',
})

// Helper to check if expected captain emails match actual captains
const isCaptainEmailMatched = (expectedEmail: string): boolean => {
  if (!expectedEmail || !props.subkrewe.captains) return false
  return props.subkrewe.captains.some(
    captain => captain.email && captain.email.toLowerCase() === expectedEmail.toLowerCase(),
  )
}

// Contact captains function
const contactExpectedCaptains = () => {
  if (!props.subkrewe.expected?.captains || props.subkrewe.expected.captains.length === 0) return

  const emails = props.subkrewe.expected.captains
    .map(captain => captain.email)
    .filter(Boolean)
    .join(',')

  if (!emails) return

  // Open email client with all expected captains in CC
  window.location.href = `mailto:${emails}?subject=Krewe of Chewbacchus - ${props.subkrewe.name} Registration`
}

// Open edit dialog
const openEditCaptainDialog = (index: number, captain: ExpectedCaptain) => {
  isAddMode.value = false
  editingIndex.value = index

  // Copy values to editing object
  editingCaptain.name = captain.name
  editingCaptain.email = captain.email
  editingCaptain.phone = captain.phone || ''

  showEditDialog.value = true
}

// Open add dialog
const openAddCaptainDialog = () => {
  isAddMode.value = true
  editingIndex.value = -1

  // Reset form
  editingCaptain.name = ''
  editingCaptain.email = ''
  editingCaptain.phone = ''

  showEditDialog.value = true
}

// Close dialog
const closeEditDialog = () => {
  showEditDialog.value = false
}

// Save changes
const saveExpectedCaptain = async () => {
  if (!editingCaptain.name || !editingCaptain.email) {
    toast.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: 'Name and email are required',
      life: 3000,
    })
    return
  }

  // Create a copy of the expected captains array with guaranteed captains array
  const updatedExpectedData = {
    ...(props.subkrewe.expected || {}),
    captains: [...(props.subkrewe.expected?.captains || [])],
  }

  // Format captain data
  const captainData: ExpectedCaptain = {
    name: editingCaptain.name,
    email: editingCaptain.email,
  }

  if (editingCaptain.phone) {
    captainData.phone = editingCaptain.phone
  }

  if (isAddMode.value) {
    // Add new captain
    updatedExpectedData.captains.push(captainData)
  }
  else {
    // Update existing captain
    updatedExpectedData.captains[editingIndex.value] = captainData
  }

  // Update the subkrewe in Firestore
  try {
    const saved = await subkrewesStore.updateSubKrewe(props.subkrewe, {
      expected: updatedExpectedData,
    })
    if (!saved) {
      throw new Error('The SubKrewe update was not saved')
    }

    toast.add({
      severity: 'success',
      summary: isAddMode.value ? 'Captain Added' : 'Captain Updated',
      detail: isAddMode.value
        ? `${captainData.name} added as an expected captain`
        : `${captainData.name}'s information has been updated`,
      life: 3000,
    })

    showEditDialog.value = false
  }
  catch (error) {
    console.error('Error updating expected captains:', error)
    toast.add({
      severity: 'error',
      summary: 'Update Failed',
      detail: 'Failed to update expected captain information',
      life: 3000,
    })
  }
}

// Remove expected captain
const removeExpectedCaptain = async () => {
  if (editingIndex.value < 0 || isAddMode.value) return

  // Create a copy of the expected captains array with guaranteed captains array
  const updatedExpectedData = {
    ...(props.subkrewe.expected || {}),
    captains: [...(props.subkrewe.expected?.captains || [])],
  }

  // Store the captain being removed for the toast message
  const removedCaptain = updatedExpectedData.captains[editingIndex.value]

  // Remove the captain at the specified index
  updatedExpectedData.captains.splice(editingIndex.value, 1)
  // Update the subkrewe in Firestore
  try {
    const saved = await subkrewesStore.updateSubKrewe(props.subkrewe, {
      expected: updatedExpectedData,
    })
    if (!saved) {
      throw new Error('The SubKrewe update was not saved')
    }

    toast.add({
      severity: 'info',
      summary: 'Captain Removed',
      detail: `${removedCaptain?.name} has been removed from expected captains`,
      life: 3000,
    })

    showEditDialog.value = false
  }
  catch (error) {
    console.error('Error removing expected captain:', error)
    toast.add({
      severity: 'error',
      summary: 'Removal Failed',
      detail: 'Failed to remove expected captain',
      life: 3000,
    })
  }
}
</script>
